import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { Box, IconButton, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import HomeIndicator from '@/designs-dev/device/HomeIndicator';
import { usePhoneScreen } from '@/designs-dev/device/PhoneScreenContext';
import { useHideTabBar } from '@/designs-dev/device/TabBarVisibility';

// Top chrome heights within the phone screen (status bar + app bar).
const SAFE_AREA_H = 47;
const APP_BAR_H = 64;
// 'full' = 100% (of the phone screen) − app bar − safe area + 20px.
const FULL_HEIGHT = `calc(100% - ${APP_BAR_H + SAFE_AREA_H - 20}px)`;

/**
 * The sheet's surface: Gardenia, the page colour, by default. A prototype can
 * try Floral White, the card colour, for every drawer under it.
 */
export const SheetSurface = createContext<'default' | 'paper'>('default');

// Generic bottom sheet that slides up over the current screen (used for action
// detail, create-action, and small action menus). Animated + dismiss-on-backdrop.
// `variant`: 'hug' sizes to content (capped at full), 'full' fills below the app bar.
const BottomSheet = ({
  title,
  titleAlign = 'left',
  onClose,
  footer,
  children,
  variant = 'full',
  showHandleClose = true,
  backdrop = true,
}: {
  title?: string;
  titleAlign?: 'left' | 'center';
  onClose: () => void;
  // A node, or a render function given the animated `close` so a footer action
  // (e.g. Save) can slide the sheet down before onClose fires.
  footer?: ReactNode | ((close: () => void) => ReactNode);
  // Same either way: a row that leads somewhere else can slide the sheet down
  // on the way, rather than being unmounted where it stands by whatever it
  // opened.
  children?: ReactNode | ((close: () => void) => ReactNode);
  variant?: 'hug' | 'full';
  showHandleClose?: boolean;
  // false = non-modal: no dim layer, the screen behind stays interactive
  // (close via the X). For sheets that assist rather than interrupt.
  backdrop?: boolean;
}) => {
  const surface = useContext(SheetSurface);
  const [open, setOpen] = useState(false);
  // Flip to open on the frame after mount so the closed state (translateY 100%)
  // paints first and the transform reliably transitions — otherwise it can
  // appear already open with no slide.
  useEffect(() => {
    const r = requestAnimationFrame(() =>
      requestAnimationFrame(() => setOpen(true)),
    );
    return () => cancelAnimationFrame(r);
  }, []);
  // The tab bar shouldn't show behind a modal.
  useHideTabBar();

  const isFull = variant === 'full';
  const screenEl = usePhoneScreen();

  const close = () => {
    setOpen(false);
    window.setTimeout(onClose, 260);
  };

  const sheet = (
    <>
      {backdrop && (
        <Box
          onClick={close}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'common.black',
            opacity: open ? 0.4 : 0,
            transition: 'opacity 260ms ease',
            // Above ModalScreen (22), not below it. A sheet is the topmost
            // layer wherever it is opened from, and Power of attorney opens one
            // from inside a modal: at 20 it slid up behind the screen that
            // asked for it, which looks like the tap did nothing.
            zIndex: 30,
          }}
        />
      )}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: isFull ? FULL_HEIGHT : 'auto',
          maxHeight: FULL_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: `background.${surface}`,
          border: 1,
          borderColor: 'custom.neutral.200',
          overflow: 'hidden',
          zIndex: 31,
          borderTopLeftRadius: '20px',
          borderTopRightRadius: '20px',
          transform: open ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 280ms ease',
        }}
      >
        {(title !== undefined || showHandleClose) && (
          <Stack
            sx={{
              alignItems: 'center',
              justifyContent:
                title === undefined
                  ? 'flex-end'
                  : titleAlign === 'center'
                    ? 'center'
                    : 'space-between',
              position: 'relative',
              px: 2,
              // Tighter under the title than over it. The header used to be an
              // even 16px both sides and the content added 12 of its own, so a
              // sheet opened with 28px between its heading and the first line
              // it was heading — enough that the two read as unrelated.
              pt: title === undefined ? 1 : 2,
              pb: title === undefined ? 1 : 1,
              flexShrink: 0,
            }}
            direction='row'
          >
            {/* h4, not h2. A sheet's title sat at 24px Lora italic over body1
                content, which is a page's heading on something that is not a
                page. h4 is one step above the copy under it, which is as much
                as a panel title has to be. */}
            {title !== undefined && (
              <Typography sx={{ color: 'primary.main' }} variant='h4'>
                {title}
              </Typography>
            )}
            {showHandleClose && (
              <IconButton
                aria-label='Close'
                onClick={close}
                sx={
                  titleAlign === 'center' && title !== undefined
                    ? { position: 'absolute', right: 8, top: 8 }
                    : undefined
                }
              >
                <Icon icon='xmark' fontSize={18} />
              </IconButton>
            )}
          </Stack>
        )}

        <Box
          sx={{
            flex: isFull ? 1 : '0 1 auto',
            minHeight: 0,
            overflow: 'auto',
            pt: 0.5,
            px: 2,
          }}
        >
          {typeof children === 'function' ? children(close) : children}
        </Box>

        {footer && (
          <Box sx={{ p: 2, flexShrink: 0 }}>
            {typeof footer === 'function' ? footer(close) : footer}
          </Box>
        )}
        <HomeIndicator />
      </Box>
    </>
  );

  return screenEl ? createPortal(sheet, screenEl) : sheet;
};

export default BottomSheet;
